import { useCallback, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import { fg } from '@atlassian/jira-feature-gating';
import {
	fireOperationalAnalytics,
	fireTrackAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { useService } from '@atlassian/jira-expansion-services/src/index.tsx';
import { requestAccess as requestAccessExpansionService } from '@atlassian/expansion-services/services';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { requestAccess } from '../../services/access-request/index.tsx';
import { fireErrorAnalytics } from '../../services/analytics/index.tsx';
import {
	getFeatureVisitedCount,
	snoozeFeatureFor30Days,
	increaseFeatureVisitedCount,
	VISITED_CONFLUENCE_LLM_COUNT_KEY,
	removeFeatureVisited,
	cjnEligibilityCacheHandler,
} from '../../services/caching/index.tsx';
import { useFilteredUsersFromCollaborationGraph } from '../../services/get-collaborators/index.tsx';
import { useNudgeStateV2a } from '../../services/nudge-state/index.tsx';
import { useNudgeRecommendations } from '../../services/recommendations/index.tsx';
import { generateCJNContent } from '../../services/generated-content/index.tsx';
import { useRequestAccessErrorFlag } from '../request-access-error-flag/index.tsx';
import {
	getContextualBanditAnalyticsAttributes,
	getContextualBanditCJNMessageParser,
	getLLMAnalyticsAttributes,
	getLLMCJNMessageParser,
	useRequestAccessPromptFlag,
} from '../request-access-prompt-flag/index.tsx';
import type { AccessRequestType } from '../../common/types.tsx';
import { useRequestAccessSuccessFlag } from '../request-access-success-flag/index.tsx';

const MAX_NUMBER_OF_CONFLUENCE_COLLABORATORS = 5;

type UseConfluenceCrossJoinNudgeArguments = {
	featureVisitedCount: number;
};

export const useConfluenceCrossJoinNudge = () => {
	const sessionId = useRef(uuid());
	const getNudgeRecommendations = useNudgeRecommendations({ sessionId: sessionId.current });
	const cloudId = useCloudId();
	const getFilteredCollaborators = useFilteredUsersFromCollaborationGraph(
		cloudId,
		'confluence',
		MAX_NUMBER_OF_CONFLUENCE_COLLABORATORS,
	);
	const showRequestAccessPromptFlag = useRequestAccessPromptFlag();
	const showSuccessFlag = useRequestAccessSuccessFlag();
	const showErrorFlag = useRequestAccessErrorFlag();
	const { setNudgeDismissed: setNudgeDismissedV2a } = useNudgeStateV2a();
	const { push } = useRouterActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const userId = useAccountId() || '';
	const [llmExperimentConfig, fireLlmExperimentExposure] = UNSAFE_noExposureExp(
		'grw-1755-growth-exp-jira-cjn-llm',
	);
	const locale = useLocale();

	const requestAccessService = fg('expansion_services_in_cjn')
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useService(requestAccessExpansionService)
		: null;

	return useCallback(
		async ({ featureVisitedCount }: UseConfluenceCrossJoinNudgeArguments, allowLlm = true) => {
			const startTimestamp = Date.now();
			try {
				async function onSubmit() {
					try {
						try {
							let accessStatus: AccessRequestType;
							if (requestAccessService && fg('expansion_services_in_cjn')) {
								const serviceResult = await requestAccessService({
									product: 'confluence',
									integration: 'crossJoinNudgesConfluence',
								});

								if (serviceResult.error) {
									fireErrorAnalytics('crossJoinNudgesConfluence', serviceResult.error);
									throw serviceResult.error;
								}
								accessStatus = serviceResult.result;
							} else {
								accessStatus = await requestAccess(
									'confluence',
									cloudId,
									userId,
									'crossJoinNudgesConfluence',
									createAnalyticsEvent,
								);
							}
							if (
								accessStatus === 'ACCESS_GRANTED' ||
								accessStatus === 'ACCESS_EXISTS' ||
								accessStatus === 'APPROVED_REQUEST_EXISTS'
							) {
								push('/wiki');
							} else if (
								accessStatus === 'PENDING_REQUEST_CREATED' ||
								accessStatus === 'PENDING_REQUEST_EXISTS'
							) {
								showSuccessFlag();
							} else {
								showErrorFlag('confluence');
							}
						} catch {
							showErrorFlag('confluence');
						}
					} catch {
						showErrorFlag('confluence');
					}
				}

				const isEnglishLocale = locale && locale.startsWith('en');

				const collaborators = await getFilteredCollaborators();
				const hasNoCollaborators = collaborators.length === 0;

				// we only need to make this api call if we know it might be used
				const llmVisitedCount = hasNoCollaborators
					? await getFeatureVisitedCount(userId, VISITED_CONFLUENCE_LLM_COUNT_KEY)
					: 0;

				// we want to prevent users who have already seen a CJN from being enrolled in the LLM CJN experiment
				const userNeverSawNonLLMCJN = featureVisitedCount === 0 || llmVisitedCount > 0;

				if (
					allowLlm &&
					isEnglishLocale &&
					hasNoCollaborators &&
					userNeverSawNonLLMCJN &&
					llmExperimentConfig.get('cohort', 'Control') === 'Variation'
				) {
					const result = await generateCJNContent();
					if (!result.ready) {
						removeFeatureVisited('crossJoinNudgesConfluence');
						return;
					}

					// Only expose the LLM experiment if content generation succeeded and would actually be shown to the user
					fireLlmExperimentExposure();
					fireTrackAnalytics(createAnalyticsEvent({}), 'feature exposed', {
						flagKey: 'grw-1755-growth-exp-jira-cjn-llm',
						cohort: 'Variation',
					});
					showRequestAccessPromptFlag({
						users: collaborators,
						onSubmit,
						onOptout: setNudgeDismissedV2a,
						snoozeFor30Days: () => snoozeFeatureFor30Days('crossJoinNudgesConfluence'),
						integration: 'crossJoinNudgesConfluence',
						featureVisitedCount: llmVisitedCount,
						messageParser: getLLMCJNMessageParser({
							title: result.title,
							description: result.body,
						}),
						eventAttributes: getLLMAnalyticsAttributes({
							users: collaborators,
							userId,
							sessionId: sessionId.current,
							featureVisitedCount,
							startTimestamp,
							companyName: result.metadata.companyName,
							firstName: result.metadata.firstName,
							generatedBody: result.body,
							generatedTitle: result.title,
						}),
					});

					// We do double book keeping on the visited counts to allow us to easily remove the LLM experiment if it fails and not orphan users who progressed halfway through it
					await Promise.all([
						increaseFeatureVisitedCount(
							userId,
							featureVisitedCount + 1,
							VISITED_CONFLUENCE_LLM_COUNT_KEY,
						),
						increaseFeatureVisitedCount(userId, featureVisitedCount + 1),
						cjnEligibilityCacheHandler.set('has_seen_conf_llm', { value: true }),
					]);
				} else {
					if (hasNoCollaborators && isEnglishLocale && allowLlm) {
						// Avoid over-exposing the control cohort of the LLM experiment by including non-english users
						fireLlmExperimentExposure();
						fireTrackAnalytics(createAnalyticsEvent({}), 'feature exposed', {
							flagKey: 'grw-1755-growth-exp-jira-cjn-llm',
							cohort: 'Control',
						});
					}
					const entityId = hasNoCollaborators ? await getNudgeRecommendations() : undefined;
					showRequestAccessPromptFlag({
						users: collaborators,
						onSubmit,
						onOptout: setNudgeDismissedV2a,
						snoozeFor30Days: () => snoozeFeatureFor30Days('crossJoinNudgesConfluence'),
						integration: 'crossJoinNudgesConfluence',
						featureVisitedCount,
						messageParser: getContextualBanditCJNMessageParser({
							isContextualBandit: hasNoCollaborators,
							entityId: entityId || 'default',
							featureVisitedCount,
						}),
						eventAttributes: getContextualBanditAnalyticsAttributes({
							users: collaborators,
							userId,
							entityId: entityId || 'default',
							sessionId: sessionId.current,
							isContextualBandit: hasNoCollaborators,
							featureVisitedCount,
							startTimestamp,
						}),
					});
					await Promise.all([
						increaseFeatureVisitedCount(userId, featureVisitedCount + 1),
						cjnEligibilityCacheHandler.set('has_seen_conf', { value: true }),
					]);
				}

				fireOperationalAnalytics(
					createAnalyticsEvent({}),
					'slo.crossJoinNudgesConfluence succeeded',
				);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics('useCrossJoinNudges', error, { cloudId });
			}
		},
		[
			cloudId,
			createAnalyticsEvent,
			getFilteredCollaborators,
			getNudgeRecommendations,
			locale,
			push,
			requestAccessService,
			setNudgeDismissedV2a,
			showErrorFlag,
			showRequestAccessPromptFlag,
			showSuccessFlag,
			userId,
			llmExperimentConfig,
			fireLlmExperimentExposure,
		],
	);
};
